import React from "react";
import { ShoppingCart, Wrench, Package } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import bgImage from "../images/Slide1.jpg";

const ThreeSDealership = () => {
  const navigate = useNavigate();

  // ✨ Simplified, ultra-smooth animation
  const fadeUp = {
    hidden: { opacity: 0, y: 70 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.9,
        ease: [0.25, 0.1, 0.25, 1], // smooth cubic-bezier easing
      },
    },
  };

  return (
    <section className="relative overflow-hidden bg-black text-white py-20 md:py-28">
      {/* High-quality background */}
      <div
        className="absolute inset-0 bg-fixed bg-cover bg-center"
        style={{ backgroundImage: `url(${bgImage})` }}
      />

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/60 to-black/90" />
      <div className="absolute inset-0">
        <div className="w-[70%] mx-auto h-full bg-[radial-gradient(circle_at_center,rgba(230,46,45,0.08),transparent_75%)]" />
      </div>

      {/* Content */}
      <div className="relative container mx-auto px-5 md:px-10 text-center">
        {/* Heading */}
        <motion.div
          className="mb-14"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">
            3S Dealership
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Authorized Isuzu dealer providing{" "}
            <span className="text-[#E62E2D] font-semibold">Sales</span>,{" "}
            <span className="text-[#E62E2D] font-semibold">Service</span>, and{" "}
            <span className="text-[#E62E2D] font-semibold">Spare Parts</span>{" "}
            for all your transportation needs.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {/* Sales */}
          <motion.div
            className="bg-white/10 backdrop-blur-lg border border-white/10 rounded-2xl shadow-lg p-8"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="flex justify-center mb-4 text-[#E62E2D]">
              <ShoppingCart size={48} />
            </div>
            <h3 className="text-2xl font-semibold mb-3 text-white">Sales</h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Wide range of Isuzu vehicles tailored to meet your business
              requirements.
            </p>
            <button
              onClick={() => navigate("/sales")}
              className="bg-[#E62E2D] text-white font-medium px-6 py-2 rounded-md hover:bg-[#cc2827] transition-colors duration-200"
            >
              Explore Vehicles
            </button>
          </motion.div>

          {/* Service */}
          <motion.div
            className="bg-white/10 backdrop-blur-lg border border-white/10 rounded-2xl shadow-lg p-8"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            transition={{ delay: 0.1 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="flex justify-center mb-4 text-[#E62E2D]">
              <Wrench size={48} />
            </div>
            <h3 className="text-2xl font-semibold mb-3 text-white">Service</h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Expert maintenance and repair services to keep your fleet running
              smoothly.
            </p>
            <button
              onClick={() => navigate("/service")}
              className="bg-[#E62E2D] text-white font-medium px-6 py-2 rounded-md hover:bg-[#cc2827] transition-colors duration-200"
            >
              Book Service
            </button>
          </motion.div>

          {/* Parts */}
          <motion.div
            className="bg-white/10 backdrop-blur-lg border border-white/10 rounded-2xl shadow-lg p-8"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            transition={{ delay: 0.2 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="flex justify-center mb-4 text-[#E62E2D]">
              <Package size={48} />
            </div>
            <h3 className="text-2xl font-semibold mb-3 text-white">Parts</h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Genuine Isuzu parts and accessories for optimal vehicle
              performance.
            </p>
            <button
              onClick={() => navigate("/parts")}
              className="bg-[#E62E2D] text-white font-medium px-6 py-2 rounded-md hover:bg-[#cc2827] transition-colors duration-200"
            >
              About Parts
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ThreeSDealership;
